import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Row, RowState } from "./Row";
import { Clue, clue } from "./clue";
import { Keyboard } from "./Keyboard";
import { maxGuesses, day1Date } from "./util";
import { makePuzzle } from "./Game";
import { fetchGameStateForPuzzle, longPollGameState, RemoteGameState } from "./gameStateApi";

interface WatchViewProps {
  puzzleId: string;
  keyboardLayout: string;
  targetUserId?: string;
  watchLabel?: string;
  onStatusChange?: (status: "in_progress" | "won" | "lost") => void;
  children?: ReactNode;
}

export function WatchView({
  puzzleId,
  keyboardLayout,
  targetUserId,
  watchLabel,
  onStatusChange,
  children,
}: WatchViewProps) {
  const [remote, setRemote] = useState<RemoteGameState | null>(null);
  const [error, setError] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const puzzleNum = parseInt(puzzleId.replace(/^practice-/, ""), 10);
  const isPractice = puzzleId.startsWith("practice-");
  const puzzle = !isNaN(puzzleNum) ? makePuzzle(puzzleNum) : null;

  useEffect(() => {
    let active = true;

    async function run() {
      if (targetUserId) {
        const data = await longPollGameState(targetUserId, puzzleId, null);
        if (!active) return;
        if (data) {
          setRemote(data);
          setError(false);
          await longPollLoop(targetUserId, data.updated_at || null);
        } else {
          setError(true);
        }
        return;
      }

      const initial = await fetchGameStateForPuzzle(puzzleId);
      if (!active) return;
      if (!initial || !initial.user_id) {
        setError(true);
        while (active) {
          await new Promise((r) => setTimeout(r, 3000));
          if (!active) return;
          const retry = await fetchGameStateForPuzzle(puzzleId);
          if (!active) return;
          if (retry && retry.user_id) {
            setRemote(retry);
            setError(false);
            await longPollLoop(retry.user_id, retry.updated_at || null);
            return;
          }
        }
        return;
      }

      setRemote(initial);
      setError(false);
      await longPollLoop(initial.user_id, initial.updated_at || null);
    }

    async function longPollLoop(userId: string, after: string | null) {
      while (active) {
        const controller = new AbortController();
        abortRef.current = controller;
        const data = await longPollGameState(userId, puzzleId, after, controller.signal);
        if (!active) return;
        if (data) {
          setRemote(data);
          after = data.updated_at || null;
        }
      }
    }

    run();

    return () => {
      active = false;
      abortRef.current?.abort();
    };
  }, [puzzleId, targetUserId]);

  useEffect(() => {
    if (onStatusChange && remote) {
      onStatusChange(remote.metadata.status);
    }
  }, [remote?.metadata.status]);

  const dateOnlyText = !isPractice && !isNaN(puzzleNum)
    ? new Date(day1Date.getTime() + (puzzleNum - 1) * 86400 * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : "";
  const dayHeader = (
    <div className="Game-options">
      {!isPractice && <span className="DayNum">Day {puzzleNum}{dateOnlyText ? `, ${dateOnlyText}` : ""}</span>}
      {isPractice && <span>Unlimited</span>}
    </div>
  );

  if (!puzzle) {
    return (
      <div className="Game">
        {dayHeader}
        <div role="alert" style={{ userSelect: "none", whiteSpace: "pre-wrap" }}>
          Loading puzzle...
        </div>
      </div>
    );
  }

  if (error && !remote) {
    return (
      <div className="Game">
        {dayHeader}
        <div role="alert" style={{ userSelect: "none", whiteSpace: "pre-wrap" }}>
          Waiting for player to start...
        </div>
      </div>
    );
  }

  if (!remote) {
    return (
      <div className="Game">
        {dayHeader}
        <div role="alert" style={{ userSelect: "none", whiteSpace: "pre-wrap" }}>
          Loading...
        </div>
      </div>
    );
  }

  const gameOver = remote.metadata.status === "won" || remote.metadata.status === "lost";
  const isLive = remote.metadata.status === "in_progress";

  const allGuesses = remote.state.guesses;
  const rawCurrentGuess = remote.state.currentGuess ?? "";
  const currentGuess = rawCurrentGuess.padEnd(puzzle.target.length, " ").slice(0, puzzle.target.length);
  const cursorPosition = remote.state.cursorPosition ?? -1;
  const safeCursorPosition =
    cursorPosition < -1
      ? -1
      : cursorPosition >= puzzle.target.length
      ? puzzle.target.length - 1
      : cursorPosition;
  const cluedRows = allGuesses.map((g) => clue(g.word, puzzle.target));

  const letterInfo = new Map<string, [Clue, number]>();
  for (const row of cluedRows) {
    for (const { clue: clueValue, letter } of row) {
      if (clueValue === undefined) break;
      const count = row.filter((cl) => cl.letter === letter && cl.clue && cl.clue > Clue.Absent).length;
      const old = letterInfo.get(letter);
      const newCount = old === undefined || old[1] < count ? count : old[1];
      const newClue = old === undefined || old[0] < clueValue ? clueValue : old[0];
      letterInfo.set(letter, [newClue, newCount]);
    }
  }

  const totalRows = gameOver ? allGuesses.length : maxGuesses;

  const tableRows = Array(totalRows)
    .fill(undefined)
    .map((_, i) => {
      if (i < allGuesses.length) {
        return (
          <Row
            key={i}
            rowState={RowState.LockedIn}
            cluedLetters={cluedRows[i]}
            cluedRows={cluedRows.slice(0, i)}
            letterInfo={letterInfo}
            rowNumber={i}
            annotation={"\u00A0"}
            currentGuess={["", -1]}
            rowWidth={puzzle.target.length}
            onClick={() => {}}
          />
        );
      }

      if (!gameOver && i === allGuesses.length) {
        return (
          <Row
            key={i}
            rowState={RowState.Editing}
            cluedLetters={clue(currentGuess, puzzle.target)}
            cluedRows={cluedRows}
            letterInfo={letterInfo}
            rowNumber={i}
            annotation={"\u00A0"}
            currentGuess={[currentGuess, safeCursorPosition]}
            rowWidth={puzzle.target.length}
            onClick={() => {}}
          />
        );
      }

      const emptyClues = Array(puzzle.target.length)
        .fill(undefined)
        .map(() => ({ letter: "", clue: undefined }));
      return (
        <Row
          key={i}
          rowState={RowState.Pending}
          cluedLetters={emptyClues}
          cluedRows={cluedRows}
          letterInfo={letterInfo}
          rowNumber={i}
          annotation={"\u00A0"}
          currentGuess={["", -1]}
          rowWidth={puzzle.target.length}
          onClick={() => {}}
        />
      );
    });

  return (
    <div className="Game" style={{ display: "block" }}>
      <div className="watch-banner-wrapper">
        <div className={`watch-banner${isLive ? " watch-banner--live" : ""}`}>
          {isLive && <span className="watch-dot" />}
          {watchLabel
            ? watchLabel
            : isLive
            ? "Watching Live"
            : gameOver
            ? remote.metadata.status === "won"
              ? "Replay — Player Won"
              : "Replay — Player Lost"
            : "Watch Mode"}
        </div>
      </div>
      <div className={`watch-border${isLive ? " watch-border--live" : ""}`}>
        <table className="Game-rows" tabIndex={0} aria-label="watched game board">
          <tbody>{tableRows}</tbody>
        </table>
      </div>
      {children}
      <Keyboard
        layout={keyboardLayout}
        letterInfo={letterInfo}
        onKey={() => {}}
      />
    </div>
  );
}
